/* sugar:assets/quick-add.js@2.1.7 */
/*
 * Quick add — the compact picker behind a product card's quick add button.
 *
 * A card whose product has more than one variant renders its quick add button with
 * data-quick-add="<product url>". A click fetches the product's JSON, fills one shared
 * picker (cloned once from the <template data-quick-add-template> the section renders) and
 * opens it: a small pop-up on desktop, a sheet from the bottom on phones. The shopper picks
 * from the option buttons, sold-out values disabled, and Add to cart submits the picker's own
 * <product-form>, so the add runs the theme's cart path (cart drawer refresh, bubble count).
 * product-form.js closes the picker through hide() and paints the cart once it has closed.
 *
 * Everything is delegated from the document, so cards that arrive through the filter refetch,
 * load more or the theme editor need no wiring.
 */
(function () {
  'use strict';

  if (window.SugarQuickAdd) return;

  var cache = {};
  var STYLES = {
    amount: [2, ',', '.'],
    amount_no_decimals: [0, ',', '.'],
    amount_with_comma_separator: [2, '.', ','],
    amount_no_decimals_with_comma_separator: [0, '.', ','],
    amount_with_apostrophe_separator: [2, "'", '.'],
    amount_with_space_separator: [2, ' ', ','],
    amount_no_decimals_with_space_separator: [0, ' ', ','],
    amount_with_period_and_space_separator: [2, ' ', '.'],
  };

  function money(cents, format) {
    if (window.SugarPricing && window.SugarPricing.formatMoney) return window.SugarPricing.formatMoney(cents, format);
    var template = format || '${{amount}}';
    var match = template.match(/\{\{\s*(\w+)\s*\}\}/);
    if (!match) return template;
    var style = STYLES[match[1]] || STYLES.amount;
    var parts = (Number(cents) / 100).toFixed(style[0]).split('.');
    var whole = parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, style[1]);
    return template.replace(match[0], parts[1] ? whole + style[2] + parts[1] : whole);
  }

  function sized(src, width) {
    if (!src) return '';
    var url = src.indexOf('//') === 0 ? 'https:' + src : src;
    return url + (url.indexOf('?') === -1 ? '?' : '&') + 'width=' + width;
  }

  function productUrl(url) {
    var a = new URL(url, window.location.href);
    return a.pathname.replace(/\/$/, '');
  }

  function fetchProduct(url) {
    var key = productUrl(url) + '.js';
    if (!cache[key]) {
      cache[key] = fetch(key, { headers: { Accept: 'application/json' } }).then(function (r) {
        if (!r.ok) throw new Error('quick add ' + r.status);
        return r.json();
      });
      cache[key].catch(function () { delete cache[key]; });
    }
    return cache[key];
  }

  /* The variant the card shows: a swatch tap points the card's links at ?variant=. */
  function cardVariant(button) {
    var card = button.closest('.sugar-card, .card-wrapper');
    var link = card && card.querySelector('.sugar-card__title a[href*="variant="], .sugar-card__media-link[href*="variant="]');
    if (!link) return null;
    return new URL(link.href, window.location.href).searchParams.get('variant');
  }

  function el(tag, cls, text) {
    var node = document.createElement(tag);
    if (cls) node.className = cls;
    if (text != null) node.textContent = text;
    return node;
  }

  class QuickAddModal extends HTMLElement {
    connectedCallback() {
      if (this._wired) return;
      this._wired = true;
      this.dialog = this.querySelector('dialog');
      this.format = this.getAttribute('data-money-format') || '';
      this.addEventListener('click', this.onClick.bind(this));
      // Chromium fires a dialog's close event on the next frame, so every way out runs onClose
      // itself; the close listener only catches a close something else started.
      this.dialog.addEventListener('cancel', function (event) {
        event.preventDefault();
        this.hide();
      }.bind(this));
      this.dialog.addEventListener('close', this.onClose.bind(this));
    }

    onClick(event) {
      if (event.target === this.dialog || event.target.closest('[data-qa-close]')) {
        this.hide();
        return;
      }
      var value = event.target.closest('[data-qa-value]');
      if (value && !value.disabled) this.pick(+value.getAttribute('data-qa-option'), value.getAttribute('data-qa-value'));
    }

    show(opener, product, variantId) {
      this.openedBy = opener;
      this.product = product;
      var variant = null;
      product.variants.forEach(function (v) {
        if (!variant && variantId && String(v.id) === String(variantId) && v.available) variant = v;
      });
      variant = variant || product.variants.filter(function (v) { return v.available; })[0] || product.variants[0];
      this.selected = variant.options.slice();
      var ratio = opener.closest('.sugar-card') && opener.closest('.sugar-card').style.getPropertyValue('--card-media-ratio');
      this.style.setProperty('--qa-ratio', ratio ? ratio.trim() : '1 / 1');
      this.querySelector('[data-qa-title]').textContent = product.title;
      this.querySelector('[data-qa-image]').alt = product.title;
      this.buildOptions();
      this.update();
      document.documentElement.classList.add('sugar-quick-add-open');
      this.shown = true;
      this.dialog.showModal();
      var first = this.querySelector('[data-qa-value]:not(:disabled)') || this.querySelector('[data-qa-close]');
      if (first) first.focus({ preventScroll: true });
    }

    /* product-form.js calls hide(true) after a successful add and paints the cart on modalClosed. */
    hide() {
      if (this.dialog.open) this.dialog.close();
      this.onClose();
    }

    onClose() {
      if (!this.shown) return;
      this.shown = false;
      document.documentElement.classList.remove('sugar-quick-add-open');
      document.body.dispatchEvent(new CustomEvent('modalClosed'));
      if (this.openedBy && this.openedBy.isConnected) this.openedBy.focus({ preventScroll: true });
    }

    buildOptions() {
      var wrap = this.querySelector('[data-qa-options]');
      wrap.textContent = '';
      var product = this.product;
      var single = product.variants.length === 1 && product.options.length === 1;
      if (single) return;
      product.options.forEach(function (option, i) {
        var name = typeof option === 'string' ? option : option.name;
        var values = typeof option === 'string' ? [] : option.values;
        var group = el('fieldset', 'sugar-quick-add__option');
        var legend = el('legend', 'sugar-quick-add__legend');
        legend.appendChild(el('span', 'sugar-quick-add__legend-name', name));
        var current = el('span', 'sugar-quick-add__legend-value');
        current.setAttribute('data-qa-current', i);
        legend.appendChild(current);
        group.appendChild(legend);
        var row = el('div', 'sugar-quick-add__values');
        values.forEach(function (value) {
          var b = el('button', 'sugar-quick-add__value', value);
          b.type = 'button';
          b.setAttribute('data-qa-option', i);
          b.setAttribute('data-qa-value', value);
          b.setAttribute('aria-pressed', 'false');
          row.appendChild(b);
        });
        group.appendChild(row);
        wrap.appendChild(group);
      });
    }

    /* A value is offered when an available variant carries it with the values picked before it. */
    availableFor(index, value) {
      var selected = this.selected;
      return this.product.variants.some(function (v) {
        if (!v.available || v.options[index] !== value) return false;
        for (var j = 0; j < index; j++) if (v.options[j] !== selected[j]) return false;
        return true;
      });
    }

    pick(index, value) {
      this.selected[index] = value;
      for (var j = index + 1; j < this.selected.length; j++) {
        if (this.availableFor(j, this.selected[j])) continue;
        var options = this.product.options[j];
        var values = options && options.values ? options.values : [];
        for (var k = 0; k < values.length; k++) {
          if (this.availableFor(j, values[k])) {
            this.selected[j] = values[k];
            break;
          }
        }
      }
      this.update();
    }

    update() {
      var self = this;
      var selected = this.selected;
      var variant = this.product.variants.filter(function (v) {
        return v.options.every(function (o, i) { return o === selected[i]; });
      })[0];
      this.variant = variant;

      Array.prototype.forEach.call(this.querySelectorAll('[data-qa-value]'), function (b) {
        var i = +b.getAttribute('data-qa-option');
        var v = b.getAttribute('data-qa-value');
        var on = selected[i] === v;
        b.setAttribute('aria-pressed', on ? 'true' : 'false');
        b.classList.toggle('is-active', on);
        var offered = self.availableFor(i, v);
        b.classList.toggle('is-unavailable', !offered);
        b.disabled = !offered && !on;
      });
      Array.prototype.forEach.call(this.querySelectorAll('[data-qa-current]'), function (span) {
        span.textContent = selected[+span.getAttribute('data-qa-current')] || '';
      });

      var product = this.product;
      var price = variant ? variant.price : product.price;
      var compare = variant ? variant.compare_at_price : product.compare_at_price;
      this.querySelector('[data-qa-price]').textContent = money(price, this.format);
      var compareEl = this.querySelector('[data-qa-compare]');
      compareEl.textContent = compare && compare > price ? money(compare, this.format) : '';
      compareEl.hidden = !(compare && compare > price);

      var image = (variant && variant.featured_image && variant.featured_image.src) || product.featured_image;
      var img = this.querySelector('[data-qa-image]');
      if (image) {
        img.src = sized(image, 360);
        img.srcset = sized(image, 180) + ' 180w, ' + sized(image, 360) + ' 360w';
        img.hidden = false;
      } else {
        img.hidden = true;
      }

      var url = (product.url || '') + (variant ? '?variant=' + variant.id : '');
      Array.prototype.forEach.call(this.querySelectorAll('[data-qa-link]'), function (a) { a.href = url; });

      var input = this.querySelector('input[name="id"]');
      var submit = this.querySelector('[data-qa-submit]');
      var label = this.querySelector('[data-qa-submit-text]');
      if (variant) input.value = variant.id;
      var buyable = !!(variant && variant.available);
      submit.disabled = !buyable;
      submit.classList.toggle('is-sold-out', !!variant && !variant.available);
      label.textContent = buyable
        ? submit.getAttribute('data-qa-add')
        : variant ? submit.getAttribute('data-qa-sold-out') : submit.getAttribute('data-qa-unavailable');
      var error = this.querySelector('.product-form__error-message-wrapper');
      if (error) error.hidden = true;
    }
  }

  if (!customElements.get('quick-add-modal')) customElements.define('quick-add-modal', QuickAddModal);

  var modal = null;
  function picker(button) {
    if (modal && modal.isConnected) return modal;
    var section = button.closest('.shopify-section');
    var template = (section && section.querySelector('template[data-quick-add-template]')) || document.querySelector('template[data-quick-add-template]');
    if (!template) return null;
    document.body.appendChild(document.importNode(template.content, true));
    modal = document.body.lastElementChild;
    if (modal && modal.tagName !== 'QUICK-ADD-MODAL') modal = document.body.querySelector('body > quick-add-modal');
    return modal;
  }

  function busy(button, on) {
    button.classList.toggle('loading', on);
    button.setAttribute('aria-busy', on ? 'true' : 'false');
    var spinner = button.querySelector('.loading__spinner');
    if (spinner) spinner.classList.toggle('hidden', !on);
  }

  function open(button) {
    if (button._qaBusy) return;
    var url = button.getAttribute('data-quick-add');
    var target = picker(button);
    if (!url || !target) {
      if (url) window.location.href = url;
      return;
    }
    button._qaBusy = true;
    busy(button, true);
    fetchProduct(url)
      .then(function (product) {
        target.show(button, product, cardVariant(button));
      })
      .catch(function () {
        window.location.href = url;
      })
      .finally(function () {
        button._qaBusy = false;
        busy(button, false);
      });
  }

  document.addEventListener('click', function (event) {
    var button = event.target.closest && event.target.closest('[data-quick-add]');
    if (!button) return;
    event.preventDefault();
    open(button);
  });

  // Warm the product JSON as a pointer heads for the button, so the picker opens at once.
  document.addEventListener('pointerover', function (event) {
    if (event.pointerType !== 'mouse') return;
    var button = event.target.closest && event.target.closest('[data-quick-add]');
    if (button && !button._qaWarm) {
      button._qaWarm = true;
      fetchProduct(button.getAttribute('data-quick-add')).catch(function () {});
    }
  });

  window.SugarQuickAdd = { open: open };
})();
